import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Ship } from 'lucide-react';
import { EntityPickerBase } from '../common';
import { getEntityUrl } from '../../utils/entityUrls';
import { useShipments } from '../../shipments/hooks/useShipmentsApi';
import type { Shipment } from '../../shipments/types';
import { getShipmentLabel } from '../../shipments/shipmentLabel';

interface ShipmentPickerProps {
  value: string;
  onChange: (shipmentId: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
  popoverClassName?: string;
}

/**
 * Autocomplete picker for shipments, searchable by reference number.
 * Built on the shared EntityPickerBase; when a shipment is selected the trigger
 * becomes a link to the shipment detail page.
 */
export function ShipmentPicker({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  allowClear = false,
  popoverClassName,
}: ShipmentPickerProps) {
  const { t } = useTranslation('shipments');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (!open && prevOpenRef.current) {
      setSearch('');
    }
    prevOpenRef.current = open;
  }, [open]);

  const { data } = useShipments({ search: search || undefined, limit: 50 });

  const shipments = useMemo(() => data?.items ?? [], [data?.items]);
  const untitled = t('untitled');

  const handleSelect = useCallback(
    (shipmentId: string) => {
      onChange(shipmentId);
      setOpen(false);
      setSearch('');
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange('');
    setOpen(false);
    setSearch('');
  }, [onChange]);

  return (
    <EntityPickerBase<Shipment>
      value={value}
      items={shipments}
      getItemLabel={(s) => getShipmentLabel(s, untitled)}
      renderItem={(shipment) => (
        <>
          <Ship className="me-1.5 h-4 w-4 text-muted-foreground shrink-0" />
          <span className="truncate">{getShipmentLabel(shipment, untitled)}</span>
        </>
      )}
      label={label}
      placeholder={placeholder ?? t('shipmentPicker.search', 'Search shipments…')}
      searchPlaceholder={t('shipmentPicker.search', 'Search shipments…')}
      emptyMessage={t('shipmentPicker.noResults', 'No shipments found')}
      disabled={disabled}
      className={className}
      search={search}
      onSearchChange={setSearch}
      open={open}
      onOpenChange={setOpen}
      onSelect={handleSelect}
      allowClear={allowClear}
      onClear={handleClear}
      selectedEntityUrl={value ? getEntityUrl({ entityType: 'shipments', entityId: value }) : null}
      entityIcon={Ship}
      popoverClassName={popoverClassName}
    />
  );
}
